import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { useQuery } from "react-query";
import {
  Text,
  Popin,
  Button,
  Table,
  NumberInput,
  Badge,
  ProductStockBadge,
} from "$components";
import { theme } from "$utils/theme/bataillog";
import useProduct from "$hooks/useProduct";
import { useCategoriesQuery } from "$hooks/useCategoriesQuery";
import useAuth from "$hooks/useAuth";
import { appState } from "$reducers";
import DefaultFilesList from "$components/FileUploader/DefaultFilesList";
import { CompanyServices, EntrepotService, ProductService } from "$services";
import { queryClient } from "$utils/queryClient";
import { Loader } from "$components/Layout/Loader";
import { IProduct } from "./Product.type";
import { useStyles } from "./styles/Product.styles";
import { CacheInvalidationAction } from "$utils/cache";

interface IProductProps {
  product?: IProduct;
  companyId?: string;
  resetProduct?: () => void;
  addToCart?: any;
  cart?: any;
  noCart?: boolean;
  substituteProduct?: any;
  edb?: string;
}

function Product({
  product,
  companyId,
  resetProduct,
  addToCart,
  cart,
  noCart,
  substituteProduct,
  edb,
}: IProductProps) {
  const classes = useStyles();
  const { isContributor } = useAuth();
  const { filter: companySidebarFilter } = useSelector(appState);

  const { data } = useCategoriesQuery(companySidebarFilter?._id || "");
  const categories = data?.orderedCategories || [];

  const { open, togglePopin, setSubProduct, subProduct } = useProduct(
    product,
    resetProduct
  );

  const activeProduct = subProduct || product;
  const effectiveCompanyId =
    companyId || activeProduct?.company || companySidebarFilter?._id;

  const { data: stockResponse } = useQuery(
    [
      CacheInvalidationAction.GET_PRODUCT_STOCK,
      activeProduct?._id,
      effectiveCompanyId,
    ],
    () =>
      ProductService.getProductStock({
        id: activeProduct?._id,
        company: effectiveCompanyId,
      }),
    {
      enabled: !!open && !!activeProduct?._id && !!effectiveCompanyId,
      staleTime: 1000 * 60,
    }
  );

  const resolvedProduct = useMemo(() => {
    if (!activeProduct) {
      return activeProduct;
    }
    const fresh = stockResponse?.data?.[0];
    if (fresh?.stock) {
      return { ...activeProduct, ...fresh, stock: fresh.stock };
    }
    return activeProduct;
  }, [activeProduct, stockResponse]);

  const {
    _id,
    designation,
    description,
    reference,
    secondReference,
    substitutionProducts,
    unit,
    stock,
    supplier,
  } = resolvedProduct ?? {};

  const quantity = cart?.find((p) => p.productID === product?._id)?.qty ?? 0;
  const [selected, setSelected] = useState<any>(null);
  const [scrollTop, setScrollTop] = useState<any>(null);
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [supplierName, setSupplierName] = useState<string>("N/A");

  const { data: warehouses } = useQuery(
    [
      CacheInvalidationAction.GET_ENTREPOTS,
      { company: companySidebarFilter?._id },
    ],
    () =>
      EntrepotService.getEntrepots({
        company: companySidebarFilter?._id,
      }),
    {
      enabled: !!companySidebarFilter?._id,
      cacheTime: 5 * 1000 * 60,
    }
  ) as any;

  const fetchData = useCallback(async () => {
    const data = await Promise.all(
      (substitutionProducts || []).map((p) => {
        return queryClient.fetchQuery(
          [CacheInvalidationAction.GET_PRODUCT_BY_ID, p],
          () => ProductService.getProductById(p),
          {
            cacheTime: 1000 * 60,
          }
        );
      })
    );

    if (data) {
      setSubProducts(
        data.filter((pr: any) => typeof pr !== "undefined" && !!pr.isReferenced)
      );
    }
  }, [substitutionProducts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Query Companies
  const getCompany = useCallback(
    async (supplier) => {
      let company;
      company = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANY_BY_ID,
        supplier,
      ]);
      if (!company) {
        company = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANY_BY_ID, supplier],
          () => CompanyServices.getCompanyById(supplier),
          { staleTime: 60 * 1000 }
        );
      }
      setSupplierName(company?.name ?? "N/A");
    },
    [setSupplierName]
  );

  useEffect(() => {
    if (supplier) {
      getCompany(supplier);
    } else {
      setSupplierName("N/A");
    }
  }, [supplier, getCompany]);

  // no render if no product provided
  if (!product) {
    return null;
  }

  const handleOnClose = () => {
    setLoading(false);
    togglePopin(false);
  };

  const extractEntreprots = (p: any) => {
    const tab = [];
    (p?.stock?.stocks || []).map((s: any) => {
      const warehouseId = s.entrepot?._id ?? s.entrepot?.id;
      const quantityLastImport = s.quantityLastImport ?? 0;

      if (
        tab.some((e) => (e.entrepot?._id ?? e.entrepot?.id) === warehouseId)
      ) {
        const index = tab.findIndex(
          (e) => (e.entrepot?._id ?? e.entrepot?.id) === warehouseId
        );
        tab[index].quantityLastImport += quantityLastImport;
        tab[index].reserved += s.reservedQuantity || 0;
      } else {
        tab.push({
          entrepot: s.entrepot,
          quantityLastImport,
          reserved: s.reservedQuantity || 0,
        });
      }
      return true;
    });

    return tab;
  };

  const WarehouseDetails = ({
    id,
    useName,
  }: {
    id: string;
    useName: boolean;
  }) => {
    const { data: entrepot, isLoading } = useQuery(
      [CacheInvalidationAction.GET_ENTREPOT_BY_ID, id],
      () => EntrepotService.getEntrepotById(id),
      {
        enabled: !!id,
        staleTime: 60 * 1000,
      }
    );
    if (isLoading) {
      return <Loader />;
    }
    if (useName) {
      return <Text>{entrepot?.name || ""}</Text>;
    }
    return (
      <>
        {entrepot?.location?.adress || ""}, {entrepot?.location?.cp || ""}{" "}
        {entrepot?.location?.city || ""}
      </>
    );
  };

  const listWarehouses = extractEntreprots(resolvedProduct);
  const body = (listWarehouses || [])
    .filter((e) =>
      isContributor()
        ? warehouses?.data?.map(({ _id }) => _id).includes(e?.entrepot?._id)
        : true
    )
    .map((e: any) => {
      (listWarehouses || []).find((x: any) => x._id === e?.entrepot?._id);
      return [
        {
          value: <WarehouseDetails id={e?.entrepot?._id} useName />,
        },
        {
          value: <WarehouseDetails id={e?.entrepot?._id} useName={false} />,
        },
        {
          value: (
            <Badge className={classes.BadgetStock} color="blue">
              {e.quantityLastImport ?? 0}
            </Badge>
          ),
        },
      ];
    });

  const productCategory =
    product.categories?.length > 0 &&
    typeof product.categories[0] === "object" &&
    "title" in product.categories[0]
      ? product.categories // Categories are already populated objects
      : categories.filter((cat) =>
          product.categories.find(
            (c) =>
              (typeof c === "string" && c === cat.id) || // Handle if still ID strings
              (typeof c === "object" && (c.id === cat.id || c._id === cat.id)) // Handle category objects
          )
        );

  const printCategories = (c) => {
    const parentCat = c.parentCategoryId
      ? categories.find((cat) => cat.id === c.parentCategoryId)
      : null;
    const grandParentCat = parentCat?.parentCategoryId
      ? categories.find((cat) => cat.id === parentCat.parentCategoryId)
      : null;

    return grandParentCat || parentCat ? (
      <ul>
        {grandParentCat && (
          <li>
            {`${grandParentCat.title} (${grandParentCat.reference})`}
            <ul>
              <li>
                {`${parentCat?.title} (${parentCat?.reference})`}
                <ul>
                  <li>
                    <b>{c.title}</b> ({c.reference})
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        )}
        {!grandParentCat && parentCat && (
          <li>
            {`${parentCat.title} (${parentCat.reference})`}
            <ul>
              <li>
                <b>{c.title}</b> ({c.reference})
              </li>
            </ul>
          </li>
        )}
      </ul>
    ) : (
      <ul>
        <li>
          <b>{c.title}</b> ({c.reference})
        </li>
      </ul>
    );
  };

  return (
    <Popin
      containerClassName={classes.ProductPopin}
      openPopin={open}
      toggleDrawer={togglePopin}
      customCloseAction={() => {
        setSubProduct(undefined);
        if (resetProduct) {
          resetProduct();
        }
        setSelected(null);
        setLoading(false);
        // togglePopin(false, true);
      }}
      scrollTop={scrollTop}
    >
      <div>
        {designation && (
          <Text className={classes.DesignationProduct} type="h2">
            {designation}
          </Text>
        )}
        {product && activeProduct?._id && effectiveCompanyId && (
          <span className={classes.Badge}>
            <ProductStockBadge
              productId={activeProduct._id}
              companyId={effectiveCompanyId}
            />
          </span>
        )}
      </div>
      <div className={classes.ProductDescription}>
        <Text>
          Description : <br />
          <b>{description || "N/A"}</b>
        </Text>
      </div>
      <div className={classes.ProductRef}>
        <Text>
          Référence principale : <b>{reference || "N/A"}</b>
        </Text>
        <Text style={{ color: theme.color.gray2 }}>{" | "}</Text>
        <Text>
          Référence secondaire : <b>{secondReference || "N/A"}</b>
        </Text>
        <Text style={{ color: theme.color.gray2 }}>{" | "}</Text>
        <Text>
          Fournisseur : <b>{supplierName || "N/A"}</b>
        </Text>
        <Text style={{ color: theme.color.gray2 }}>{" | "}</Text>
        <Text>
          Unité : <b>{unit || "N/A"}</b>
        </Text>
      </div>
      <div className={classes.ProductRef}>
        <Text>Catégories : {(productCategory || []).map(printCategories)}</Text>
      </div>
      {product?.medias && product?.medias?.length > 0 && (
        <div>
          <Text>Pièces jointes : </Text>
          <DefaultFilesList axis="xy" items={product?.medias} />
        </div>
      )}
      {!noCart && (
        <div className={classes.ProductQtyInput}>
          <NumberInput
            value={quantity}
            onChange={(val) => addToCart(product._id, val)}
          />
        </div>
      )}
      {/* Table */}
      {(stock?.quantity || 0) > 0 && (
        <div className={classes.ProductAvailabilty}>
          <Text type="h3">Disponibilité de l&apos;article</Text>
          <Table
            bodyData={body}
            rowsPerPage={20}
            thead={entrepotHead}
            className={classes.Product__EntrepotTable}
          />
        </div>
      )}
      {/* Substitution product */}
      {!!subProducts.length && (
        <div className={classes.ProductSubstitutions}>
          <Text type="h3">Produit(s) de substitution</Text>
          <div className={classes.SubstitutionList}>
            {subProducts &&
              subProducts.map((p: any) =>
                p?._id ? (
                  <div
                    key={p?._id || ""}
                    onClick={() => {
                      setSubProduct(p);
                      setScrollTop((prevState: boolean) => !prevState);
                    }}
                    className={classes.SubstitutionItem}
                  >
                    <div>
                      <Text type="important">{p?.designation || ""}</Text>
                    </div>
                    <div className={classes.SubstituteRef}>
                      <div>
                        <Text>Référence principale</Text>
                        <div>
                          <Text className={classes.Unit}>
                            {p?.reference || ""}
                          </Text>
                        </div>
                      </div>

                      {p?.secondReference !== "" && (
                        <div>
                          <Text>Référence secondaire</Text>
                          <div>
                            <Text className={classes.Unit}>
                              {p?.secondReference}
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>
                    <span>
                      <Text>Unité :</Text>
                      <span>
                        <Text className={classes.Unit}>{p?.unit || ""}</Text>
                      </span>
                    </span>
                  </div>
                ) : null
              )}
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
        }}
      >
        <Button isDefault variant="outlined" onClick={handleOnClose}>
          Retour
        </Button>
        {selected && (
          <Button
            onClick={async () => {
              if (selected && substituteProduct && edb) {
                setLoading(true);
                const res = await substituteProduct(
                  edb,
                  _id,
                  selected?._id || ""
                );
                if (res?.error) {
                  return;
                }
                togglePopin(false, true);
                setSelected(false);
              }
            }}
            aria-label="Substituer le produit"
          >
            {loading && (
              <CircularProgress
                style={{ marginRight: "4px", height: "30px", width: "30px" }}
              />
            )}
            Substituer le produit
          </Button>
        )}
      </div>
    </Popin>
  );
}

export default Product;

const entrepotHead = [
  { id: "entrepot", label: "entrepôt" },
  {
    id: "adresse",
    label: "adresse",
  },
  {
    id: "qty",
    label: (
      <>
        qté en stock
        <br />
        hors réservation
      </>
    ),
  },
];
